import { SyntheticEvent, useRef } from 'react';
import { cloneDeep, merge } from 'lodash';
import BootstrapOverlay, { OverlayChildren, OverlayInjectedProps, Placement } from 'react-bootstrap/Overlay';
import BootstrapPopover from 'react-bootstrap/Popover';

import './OverlayTrigger.scss';

// Disabling preventOverflow can make the dropdown static, which fits our use cases.
const DEFAULT_POPPER_CONFIG = {
  modifiers: [
    {
      name: 'preventOverflow',
      options: {
        mainAxis: false,
        altAxis: false,
      },
    },
  ],
};

type OverlayTriggerProps = {
  placement: Placement;
  popperConfig?: object;
  overlay: JSX.Element;
  children: JSX.Element;
  className?: string;
  showOverlay: boolean;
  setShowOverlay: (showValue: boolean, e?: Event | SyntheticEvent) => void;
  rootClose?: boolean;
  onHide?: (e?: Event | SyntheticEvent) => void;
  rootCloseEvent?: 'click' | 'mousedown' | undefined;
  popoverClass?: string;
  onEntered?: () => void;
  disabled?: boolean;
};

/**
 * A wrapper component around Bootstrap Overlay Trigger that uses Popover component as the overlay.
 *
 * @prop placement           Where to place the popover.
 * @prop popperConfig        A config object to overwrite Bootstrap internal popper config.
 * @prop overlay             The overlay element.
 * @prop children            The overlay trigger element.
 * @prop className           Class to apply to container DIV
 * @prop showOverlay         The overlay state. If true, show the overlay.
 * @prop setShowOverlay      The overlay state setter to control the show/hide state of overlay.
 * @prop rootClose           Specify whether the overlay should trigger onHide when the user clicks outside the overlay
 * @prop onHide              Function to run when the overlay is going to hide due to outside click
 * @prop rootCloseEvent      Specify event for triggering a "root close" toggle.
 * @prop popoverClass        Class to apply to the popover container
 * @prop disabled            If true, clicking on the triggering element does nothing.
 * @prop onEntered           Callback fired after the Overlay finishes transitioning in
 */
function OverlayTrigger({
  placement,
  popperConfig,
  overlay,
  children,
  className,
  showOverlay,
  setShowOverlay,
  rootClose = true,
  rootCloseEvent,
  popoverClass,
  onHide = (e?: Event | SyntheticEvent): void => {
    setShowOverlay(false, e);
  },
  onEntered,
  disabled = false,
}: OverlayTriggerProps): JSX.Element {
  // This ref is attached to triggeringElement and BootstrapOverlay so it will open the overlay in the expected position.
  const target = useRef<HTMLDivElement>(null);

  const triggeringElement = (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={target}
      onClick={(e: SyntheticEvent): void => {
        if (!disabled) {
          setShowOverlay(true, e);
        }
      }}
      className={className}
    >
      {children}
    </div>
  );

  const popover: OverlayChildren = ({ show, arrowProps, popper, ...otherProps }: OverlayInjectedProps) => (
    // A bug in React-bootstrap. Popover does not accept show prop.
    // https://github.com/react-bootstrap/react-overlays/issues/312#issuecomment-558561867
    // use bsPrefix="Scalyr" to forgo the Bootstrap style.
    //
    // 2022-06-10: MUI upgrade causes an issue with types due to an update to underlying common dep `csstypes`
    <BootstrapPopover
      id="Popover"
      arrowProps={arrowProps}
      {...otherProps}
      className={`OverlayTrigger__Popover ${popoverClass}`}
      bsPrefix="Scalyr"
    >
      {overlay}
    </BootstrapPopover>
  );
  const rootCloseEventDefault = rootClose ? 'click' : undefined;

  return (
    <>
      {triggeringElement}
      <BootstrapOverlay
        placement={placement}
        popperConfig={merge(cloneDeep(DEFAULT_POPPER_CONFIG), popperConfig)}
        target={target.current}
        show={showOverlay}
        rootClose={rootClose}
        onEntered={onEntered}
        rootCloseEvent={rootCloseEvent || rootCloseEventDefault}
        onHide={onHide}
      >
        {popover}
      </BootstrapOverlay>
    </>
  );
}

export default OverlayTrigger;
